import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Empty, Row, Spin, Tag, Typography } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import apiClient from '../../../services/apiClient';
import CallPutOptionsDetails, { hasCallPutFieldValue } from '../CallPutOptionsDetails';
import '../CallPutOptionsDetails.css';

const { Text } = Typography;
const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function optionLabel(available) {
  return (available ? 'Yes' : 'No').toUpperCase();
}

function redemptionDisplay(optionsData, holding) {
  const fromOptions = optionsData?.callRedemptionDate
    || optionsData?.putRedemptionDate;
  if (hasCallPutFieldValue(fromOptions)) return fromOptions;
  if (holding?.maturityDate) return holding.maturityDate;
  return 'N/A';
}

function hasAnyCallPutOptions(optionsData) {
  return hasCallPutFieldValue(optionsData?.callSpecifiedDates)
    || hasCallPutFieldValue(optionsData?.putSpecifiedDates);
}

async function fetchOptionsForHolding(holding) {
  try {
    const res = await apiClient.get(`${BASE_URL}/master-isin/${holding.isin}/options`);
    const data = res.data?.data ?? null;
    return {
      isin: holding.isin,
      issuer: holding.issuer,
      holding,
      optionsData: data,
      error: data ? null : 'No embedded options information available.',
    };
  } catch (e) {
    return {
      isin: holding.isin,
      issuer: holding.issuer,
      holding,
      optionsData: null,
      error: e.response?.data?.message || e.message || 'Error fetching options data',
    };
  }
}

function PortfolioCorporateActionCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const { optionsData, error, holding } = item;

  const callAvailable = hasCallPutFieldValue(optionsData?.callSpecifiedDates);
  const putAvailable = hasCallPutFieldValue(optionsData?.putSpecifiedDates);
  const canExpand = Boolean(optionsData) && !error;

  return (
    <article className="mpd-corp-isin-card">
      <header className="mpd-corp-isin-head">
        <div>
          <Text code className="mpd-corp-isin-code">{item.isin}</Text>
          {item.issuer && (
            <Text type="secondary" className="mpd-corp-isin-issuer">{item.issuer}</Text>
          )}
        </div>
        {canExpand && (
          <Button type="link" size="small" onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? 'Show less' : 'See more'}
          </Button>
        )}
      </header>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <div className="mpd-corp-summary-item">
            <Text type="secondary">Call Option</Text>
            <div>
              <Tag color="blue">{optionLabel(callAvailable)}</Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} md={8}>
          <div className="mpd-corp-summary-item">
            <Text type="secondary">Put Option</Text>
            <div>
              <Tag color="blue">{optionLabel(putAvailable)}</Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} md={8}>
          <div className="mpd-corp-summary-item">
            <Text type="secondary">Redemption / Maturity</Text>
            <div>
              <Text strong>{redemptionDisplay(optionsData, holding)}</Text>
            </div>
          </div>
        </Col>
      </Row>

      {error && !callAvailable && !putAvailable && (
        <div className="mpd-corp-error">
          <Text type="secondary">{error}</Text>
        </div>
      )}

      {expanded && canExpand && (
        <div className="mpd-corp-details">
          <CallPutOptionsDetails data={optionsData} />
        </div>
      )}
    </article>
  );
}

export default function PortfolioCorporateActionsTab({ holdings }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const loadAll = useCallback(async () => {
    if (!holdings?.length) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all(holdings.map(fetchOptionsForHolding));
      setItems(results);
    } catch (e) {
      console.error('Failed to load corporate actions', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [holdings]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const visibleItems = useMemo(
    () => items.filter((item) => hasAnyCallPutOptions(item.optionsData)),
    [items],
  );

  if (!holdings?.length) {
    return <Empty description="Add bond holdings to view corporate actions" />;
  }

  return (
    <div className="mpd-tab-panel">
      <div className="mpd-tab-panel-head">
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            <InfoCircleOutlined style={{ marginRight: 8, color: '#0369a1' }} />
            Upcoming Put &amp; Call options
          </Typography.Title>
          <Text type="secondary">
            Availability summary per ISIN — expand for full call and put option details.
          </Text>
        </div>
        <Tag color={visibleItems.length ? 'processing' : 'default'}>
          {visibleItems.length} ISIN{visibleItems.length === 1 ? '' : 's'} with options
        </Tag>
      </div>

      {loading ? (
        <div className="mpd-tab-loading">
          <Spin indicator={<LoadingOutlined spin />} />
          <Text type="secondary">Loading corporate actions…</Text>
        </div>
      ) : visibleItems.length === 0 ? (
        <Empty description="No put or call options found for your holdings" />
      ) : (
        <div className="mpd-corp-list">
          {visibleItems.map((item) => (
            <PortfolioCorporateActionCard key={item.isin} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
